import { z } from 'zod';

// Promo code types
export enum PromoCodeType {
  TRIAL = 'trial',
  DISCOUNT = 'discount',
  CREDIT = 'credit',
}

export enum DiscountType {
  PERCENTAGE = 'percentage',
  FIXED_AMOUNT = 'fixed_amount',
}

// Promo Code Schema
export const PromoCodeSchema = z.object({
  id: z.string().uuid(),
  code: z.string().max(50),
  description: z.string().nullable().optional(),
  type: z.nativeEnum(PromoCodeType),
  discountType: z.nativeEnum(DiscountType).nullable().optional(),
  discountValue: z.number().nullable().optional(),
  trialDays: z.number().int().positive().nullable().optional(),
  creditAmount: z.number().positive().nullable().optional(),
  currency: z.string().length(3).default('USD'),
  maxUses: z.number().int().positive().nullable().optional(),
  currentUses: z.number().int().min(0).default(0),
  validFrom: z.date(),
  validUntil: z.date().nullable().optional(),
  applicablePlans: z.array(z.string()).default([]),
  minimumAmount: z.number().positive().nullable().optional(),
  firstTimeOnly: z.boolean().default(false),
  metadata: z.record(z.any()).default({}),
  isActive: z.boolean().default(true),
  createdAt: z.date().optional(),
  updatedAt: z.date().optional(),
  creatorId: z.string().uuid(),
});

export type PromoCode = z.infer<typeof PromoCodeSchema>;

// Create Promo Code DTO
export const CreatePromoCodeDtoSchema = z.object({
  code: z.string().max(50).regex(/^[A-Z0-9_-]+$/i, 'Code must contain only letters, numbers, underscores, and hyphens'),
  description: z.string().optional(),
  type: z.nativeEnum(PromoCodeType),
  discountType: z.nativeEnum(DiscountType).optional(),
  discountValue: z.number().optional(),
  trialDays: z.number().int().positive().optional(),
  creditAmount: z.number().positive().optional(),
  currency: z.string().length(3).default('USD'),
  maxUses: z.number().int().positive().optional(),
  validFrom: z.date().default(() => new Date()),
  validUntil: z.date().optional(),
  applicablePlans: z.array(z.string()).optional(),
  minimumAmount: z.number().positive().optional(),
  firstTimeOnly: z.boolean().optional(),
  metadata: z.record(z.any()).optional(),
}).refine((data) => {
  // Validate based on type
  if (data.type === PromoCodeType.TRIAL) {
    return data.trialDays && data.trialDays > 0;
  }
  if (data.type === PromoCodeType.DISCOUNT) {
    return data.discountType && data.discountValue && data.discountValue > 0;
  }
  if (data.type === PromoCodeType.CREDIT) {
    return data.creditAmount && data.creditAmount > 0;
  }
  return false;
}, {
  message: 'Invalid promo code configuration for the selected type',
});

export type CreatePromoCodeDto = z.infer<typeof CreatePromoCodeDtoSchema>;

// Update Promo Code DTO
export const UpdatePromoCodeDtoSchema = z.object({
  description: z.string().optional(),
  maxUses: z.number().int().positive().optional(),
  validUntil: z.date().optional(),
  applicablePlans: z.array(z.string()).optional(),
  minimumAmount: z.number().positive().optional(),
  isActive: z.boolean().optional(),
  metadata: z.record(z.any()).optional(),
});

export type UpdatePromoCodeDto = z.infer<typeof UpdatePromoCodeDtoSchema>;

// Promo Code Usage Schema
export const PromoCodeUsageSchema = z.object({
  id: z.string().uuid(),
  promoCodeId: z.string().uuid(),
  organizationId: z.string().uuid(),
  subscriptionId: z.string().uuid().nullable().optional(),
  userId: z.string().uuid(),
  stripeCustomerId: z.string().nullable().optional(),
  stripeCouponId: z.string().nullable().optional(),
  discountApplied: z.number().nullable().optional(),
  trialDaysApplied: z.number().int().nullable().optional(),
  creditApplied: z.number().nullable().optional(),
  metadata: z.record(z.any()).default({}),
  usedAt: z.date(),
});

export type PromoCodeUsage = z.infer<typeof PromoCodeUsageSchema>;

// Apply Promo Code DTO
export const ApplyPromoCodeDtoSchema = z.object({
  code: z.string(),
  planId: z.string().uuid().optional(), // For validating if code applies to the plan
});

export type ApplyPromoCodeDto = z.infer<typeof ApplyPromoCodeDtoSchema>;

// Promo Code Validation Result
export interface PromoCodeValidationResult {
  valid: boolean;
  code?: PromoCode;
  error?: string;
  discount?: {
    type: DiscountType;
    value: number;
    amount?: number; // Calculated discount amount for fixed discounts
  };
  trial?: {
    days: number;
  };
  credit?: {
    amount: number;
    currency: string;
  };
}

// Promo Code Examples
export const PROMO_CODE_EXAMPLES = {
  // 30-day trial
  TRIAL30: {
    code: 'TRIAL30',
    type: PromoCodeType.TRIAL,
    trialDays: 30,
    description: '30-day free trial',
  },
  // 90-day trial
  TRIAL90: {
    code: 'TRIAL90',
    type: PromoCodeType.TRIAL,
    trialDays: 90,
    description: '90-day free trial',
  },
  // 1-year trial
  TRIAL365: {
    code: 'TRIAL365',
    type: PromoCodeType.TRIAL,
    trialDays: 365,
    description: '1-year free trial',
  },
  // 20% off
  SAVE20: {
    code: 'SAVE20',
    type: PromoCodeType.DISCOUNT,
    discountType: DiscountType.PERCENTAGE,
    discountValue: 20,
    description: '20% off subscription',
  },
  // $50 off
  SAVE50: {
    code: 'SAVE50',
    type: PromoCodeType.DISCOUNT,
    discountType: DiscountType.FIXED_AMOUNT,
    discountValue: 50,
    description: '$50 off subscription',
  },
  // $100 credit
  CREDIT100: {
    code: 'CREDIT100',
    type: PromoCodeType.CREDIT,
    creditAmount: 100,
    description: '$100 account credit',
  },
};